import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Switch, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { TemplatesViewModel } from '../../../../extViewmodels';
import { dedupeByProvider } from '../../../../extViewmodels/Utils';
import { getSocialMediaPlainTextContentFromRawRichContent } from '../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { CreateGhostCalendarSuggestionViewModel } from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components/Button';
import { PrivateRoute } from '../../../../web/components/PrivateRoute';
import { RichContentDocumentEditor } from '../../../../web/components/richContent/RichContentDocumentEditor';
import { PreviewSocialMediaPost } from '../../../../web/components/socialMedia/PreviewSocialMediaPost';
import { GhostCalendarSubverticals } from '../../../components/GhostCalendarSubverticals';
import { GhostCalendarSummary } from '../../../components/GhostCalendarSummary';
import { GhostCalendarTags } from '../../../components/GhostCalendarTags';
import { GhostCalendarTargets } from '../../../components/GhostCalendarTargets';
import { EditGhostCalendarSuggestionStep } from './models';
import { styleSheet } from './styles';

interface IGhostCalendarProps {
	createSuggestion?: CreateGhostCalendarSuggestionViewModel;
	onNext?: () => void;
	onSave?: () => void;
	canGoNext?: boolean;
}

export const GhostCalendarEmailSuggestion: React.FC<IGhostCalendarProps> = observer(props => {
	const { createSuggestion, onSave, canGoNext, onNext } = props;
	const match = useRouteMatch();
	const routerLocation = useLocation();
	const { step } = useParams<{ step?: EditGhostCalendarSuggestionStep }>();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	React.useEffect(() => {
		if (!createSuggestion?.template?.content && createSuggestion?.templateReference?.templateId) {
			new TemplatesViewModel(userSession)
				.getById(createSuggestion.templateReference.templateId)
				.then(template => {
					createSuggestion.template = template;
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
	}, [createSuggestion, errorMessages, userSession]);

	return (
		<>
			<div className={css(styleSheet.bodyField)}>
				<header className={css(styleSheet.header)}>
					<h3 className={css(styleSheet.name)}>{createSuggestion?.template?.name}</h3>
					<p className={css(styleSheet.infoContainer)}>
						We default to send from Contact Owners to be safe. Users can always change this.
					</p>
				</header>
				<RichContentDocumentEditor
					className={css(styleSheet.editor)}
					contentState={createSuggestion?.templateContent}
					readOnly={true}
					showFirstNameTokenByDefault={true}
				/>
			</div>
			<div className={css(styleSheet.sideBar)}>
				<div className={css(styleSheet.sideBarContent)}>
					<Switch>
						<PrivateRoute
							location={routerLocation}
							path={`${match.path}/${EditGhostCalendarSuggestionStep.Summary}`}
							userSession={userSession}
						>
							<GhostCalendarSummary createSuggestion={createSuggestion} />
						</PrivateRoute>
						<PrivateRoute
							location={routerLocation}
							path={`${match.path}/${EditGhostCalendarSuggestionStep.Tags}`}
							userSession={userSession}
						>
							<GhostCalendarTags createSuggestion={createSuggestion} />
						</PrivateRoute>
						<PrivateRoute
							location={routerLocation}
							path={`${match.path}/${EditGhostCalendarSuggestionStep.Subverticals}`}
							userSession={userSession}
						>
							<GhostCalendarSubverticals createSuggestion={createSuggestion} />
						</PrivateRoute>
					</Switch>
				</div>
				{step !== EditGhostCalendarSuggestionStep.Summary ? (
					<Button
						className={css(styleSheet.button)}
						type='button'
						label={
							createSuggestion?.isEditing
								? 'Okay'
								: step === EditGhostCalendarSuggestionStep.Subverticals
									? 'Next: Select Tags'
									: 'Next: Send Options'
						}
						onClick={createSuggestion?.isEditing ? onSave : onNext}
						disabled={!canGoNext || createSuggestion?.isBusy}
					/>
				) : null}
			</div>
		</>
	);
});

export const GhostCalendarSocialSuggestion: React.FC<IGhostCalendarProps> = observer(props => {
	const { createSuggestion, onSave, canGoNext, onNext } = props;
	const match = useRouteMatch();
	const routerLocation = useLocation();
	const { step } = useParams<{ step?: EditGhostCalendarSuggestionStep }>();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const postContent = getSocialMediaPlainTextContentFromRawRichContent(createSuggestion?.template?.content);
	const postImages = createSuggestion?.template?.attachments;

	React.useEffect(() => {
		if (!createSuggestion?.template?.content && createSuggestion?.templateReference?.templateId) {
			new TemplatesViewModel(userSession)
				.getById(createSuggestion.templateReference.templateId)
				.then(template => {
					createSuggestion.template = template;
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
	}, [createSuggestion, errorMessages, userSession]);

	return (
		<>
			<header className={css(styleSheet.header)}>
				<h3 className={css(styleSheet.name)}>{createSuggestion?.template?.name}</h3>
				{createSuggestion?.template?.summary ? (
					<p className={css(styleSheet.infoContainer)}>{createSuggestion?.template?.summary}</p>
				) : null}
			</header>
			<div className={css(styleSheet.mainContent)}>
				<div className={css(styleSheet.bodyField, styleSheet.bodyFieldSocial)}>
					<PreviewSocialMediaPost
						postTargets={dedupeByProvider(createSuggestion?.targets)}
						overlayLogo={false}
						hideWhiteBackground={false}
						postContent={postContent}
						postImages={postImages}
						impersonationCtx={createSuggestion?.impersonationContext}
						postCreatorUserId={createSuggestion?.template?.creator?.id}
					/>
				</div>
				<div className={css(styleSheet.sideBar)}>
					<div className={css(styleSheet.sideBarContent)}>
						<Switch>
							<PrivateRoute
								location={routerLocation}
								path={`${match.path}/${EditGhostCalendarSuggestionStep.Summary}`}
								userSession={userSession}
							>
								<GhostCalendarSummary createSuggestion={createSuggestion} />
							</PrivateRoute>
							<PrivateRoute
								location={routerLocation}
								path={`${match.path}/${EditGhostCalendarSuggestionStep.Subverticals}`}
								userSession={userSession}
							>
								<GhostCalendarSubverticals createSuggestion={createSuggestion} />
							</PrivateRoute>
							<PrivateRoute
								location={routerLocation}
								path={`${match.path}/${EditGhostCalendarSuggestionStep.Targets}`}
								userSession={userSession}
							>
								<GhostCalendarTargets createSuggestion={createSuggestion} />
							</PrivateRoute>
						</Switch>
					</div>
					{step !== EditGhostCalendarSuggestionStep.Summary ? (
						<Button
							className={css(styleSheet.button)}
							type='button'
							label={
								createSuggestion?.isEditing
									? 'Okay'
									: step === EditGhostCalendarSuggestionStep.Subverticals
										? 'Next: Select Targets'
										: 'Next: Send Options'
							}
							onClick={createSuggestion?.isEditing ? onSave : onNext}
							disabled={!canGoNext || createSuggestion?.isBusy}
						/>
					) : null}
				</div>
			</div>
		</>
	);
});

export const GhostCalendarBlogSuggestion: React.FC<IGhostCalendarProps> = observer(props => {
	const { createSuggestion, onSave, canGoNext, onNext } = props;
	const match = useRouteMatch();
	const routerLocation = useLocation();
	const { step } = useParams<{ step?: EditGhostCalendarSuggestionStep }>();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();

	React.useEffect(() => {
		if (!createSuggestion?.template?.content && createSuggestion?.templateReference?.templateId) {
			new TemplatesViewModel(userSession)
				.getById(createSuggestion.templateReference.templateId)
				.then(template => {
					createSuggestion.template = template;
				})
				.catch(err => {
					errorMessages.pushApiError(err);
				});
		}
	}, [createSuggestion, errorMessages, userSession]);

	return (
		<>
			<div className={css(styleSheet.bodyField)}>
				<header className={css(styleSheet.header)}>
					<h3 className={css(styleSheet.name)}>{createSuggestion?.template?.name}</h3>
				</header>
				<RichContentDocumentEditor
					className={css(styleSheet.editor)}
					contentState={createSuggestion?.templateContent}
					readOnly={true}
				/>
			</div>
			<div className={css(styleSheet.sideBar)}>
				<div className={css(styleSheet.sideBarContent)}>
					<Switch>
						<PrivateRoute
							location={routerLocation}
							path={`${match.path}/${EditGhostCalendarSuggestionStep.Summary}`}
							userSession={userSession}
						>
							<GhostCalendarSummary createSuggestion={createSuggestion} />
						</PrivateRoute>
						<PrivateRoute
							location={routerLocation}
							path={`${match.path}/${EditGhostCalendarSuggestionStep.Subverticals}`}
							userSession={userSession}
						>
							<GhostCalendarSubverticals createSuggestion={createSuggestion} />
						</PrivateRoute>
					</Switch>
				</div>
				{step !== EditGhostCalendarSuggestionStep.Summary ? (
					<Button
						className={css(styleSheet.button)}
						type='button'
						label={createSuggestion?.isEditing ? 'Okay' : 'Next: Schedule Post'}
						onClick={createSuggestion?.isEditing ? onSave : onNext}
						disabled={!canGoNext || createSuggestion?.isBusy}
					/>
				) : null}
			</div>
		</>
	);
});
